/**
 * Common power monitoring capabilities
 * Used by relays and dimmers with power measurement
 */

/**
 * Power consumption condition
 */
export function createPowerLevelCondition(maxPower = 3680) {
	return {
		name: 'Power Consumption',
		description: 'Check current power consumption level',
		parameters: {
			operator: {
				type: 'enum',
				required: true,
				values: ['gt', 'lt', 'gte', 'lte', 'eq'],
				description: 'Comparison operator',
			},
			power: {
				type: 'number',
				required: true,
				min: 0,
				max: maxPower,
				unit: 'W',
				description: 'Power threshold in watts',
			},
			hysteresis: {
				type: 'number',
				required: false,
				default: 5,
				unit: 'W',
				description: 'Hysteresis value to prevent flapping',
			},
		},
		evaluateCondition: (device, channel, params) => {
			const currentPower = device.status?.[channel]?.apower || 0;
			const { operator, power, hysteresis = 5 } = params;

			switch (operator) {
				case 'gt':
					return currentPower > power + hysteresis;
				case 'lt':
					return currentPower < power - hysteresis;
				case 'gte':
					return currentPower >= power;
				case 'lte':
					return currentPower <= power;
				case 'eq':
					return Math.abs(currentPower - power) <= hysteresis;
				default:
					return false;
			}
		},
	};
}

/**
 * Power threshold crossing trigger
 */
export function createPowerThresholdTrigger(maxPower = 3680) {
	return {
		name: 'Power Threshold',
		description: 'Fires when power consumption crosses a threshold',
		parameters: {
			threshold: {
				type: 'number',
				required: true,
				min: 0,
				max: maxPower,
				unit: 'W',
				description: 'Power threshold in watts',
			},
			direction: {
				type: 'enum',
				required: true,
				values: ['above', 'below', 'both'],
				default: 'both',
				description: 'Threshold crossing direction',
			},
			debounce: {
				type: 'number',
				required: false,
				default: 1000,
				unit: 'ms',
				description: 'Debounce time in milliseconds',
			},
		},
		detectTrigger: (previousState, currentState, channel, params) => {
			const prevPower = previousState?.status?.[channel]?.apower || 0;
			const currPower = currentState?.status?.[channel]?.apower || 0;
			const { threshold, direction = 'both' } = params;

			if (direction === 'above' || direction === 'both') {
				if (prevPower <= threshold && currPower > threshold) return true;
			}

			if (direction === 'below' || direction === 'both') {
				if (prevPower >= threshold && currPower < threshold) return true;
			}

			return false;
		},
	};
}
